/**
 * The document view's geometry, kept pure so it can be pinned by tests.
 *
 * Two halves. The diagram: which findings sit on a page, which column each
 * card goes to, and how far down it sits — computed at fit size, so the cards
 * never move. The view: how the page is zoomed and panned inside its frame,
 * and where a point on the page lands on screen once it is.
 *
 * The diagram is ported from the legacy console's `AnnotatedDocument`; the
 * view is new.
 */

import type { FindingId, PointRect } from "../../data/review-data"
import type { ReportFindingItem } from "../report-data"
import { clamp } from "../use-pdf-page"

/** Each callout column's width, the space between it and the page, and between cards. */
export const CARD_WIDTH = 240
export const GUTTER = 28
export const CARD_GAP = 12
/** A card is assumed this tall until it has been measured. */
export const ESTIMATED_CARD_HEIGHT = 96
/** Below this much room for the page between the columns, the cards are dropped. */
export const MIN_PAGE_WIDTH = 320

/** A box on the page, as fractions of the page's width and height. */
export interface Fraction {
  x: number
  y: number
  w: number
  h: number
}

export interface Pin {
  finding: ReportFindingItem
  /** Every occurrence of the finding on this page, in label order. */
  boxes: Fraction[]
  /** Where the card's line lands: the centre of the first box. */
  x: number
  y: number
}

export interface PlacedCard {
  id: FindingId
  side: "left" | "right"
  /** Card top, in pixels from the top of the diagram. */
  top: number
}

export function toFraction(rect: PointRect, pointWidth: number, pointHeight: number): Fraction {
  return {
    x: rect.x0 / pointWidth,
    y: rect.y0 / pointHeight,
    w: (rect.x1 - rect.x0) / pointWidth,
    h: (rect.y1 - rect.y0) / pointHeight,
  }
}

/** The findings with at least one occurrence on `page` (1-based), each with all of its boxes there. */
export function pinsOnPage(
  findings: readonly ReportFindingItem[],
  page: number,
  pointWidth: number,
  pointHeight: number,
): Pin[] {
  const pins: Pin[] = []
  for (const finding of findings) {
    const boxes = finding.locations
      .filter((rect) => rect.page === page)
      .map((rect) => toFraction(rect, pointWidth, pointHeight))
    const first = boxes[0]
    if (!first) continue
    pins.push({ finding, boxes, x: first.x + first.w / 2, y: first.y + first.h / 2 })
  }
  return pins
}

export interface Mark {
  pin: Pin
  box: Fraction
  /** Which of the finding's boxes on the page. */
  index: number
}

/**
 * Every box on the page, in the order they are drawn — last on top.
 *
 * Several findings often share one printed line. The report's own box for
 * each goes on in finding order, so the highest number's badge shows, as on
 * the legacy report. The further occurrences, which that report never drew,
 * go underneath them all, so none of them can hide a finding that sits
 * there. A selected finding comes to the top, so its boxes are never
 * covered.
 */
export function marksInDrawOrder(pins: readonly Pin[], selected: FindingId | null): Mark[] {
  const further: Mark[] = []
  const own: Mark[] = []
  const lifted: Mark[] = []
  for (const pin of [...pins].sort((a, b) => a.finding.number - b.finding.number)) {
    pin.boxes.forEach((box, index) => {
      const mark = { pin, box, index }
      if (pin.finding.id === selected) lifted.push(mark)
      else if (index === 0) own.push(mark)
      else further.push(mark)
    })
  }
  return [...further, ...own, ...lifted]
}

/**
 * A finding the label has no position for. It still has to be reachable:
 * dropping it because nothing could place it would quietly hide a divergence.
 */
export function unpinnedFindings(findings: readonly ReportFindingItem[]): ReportFindingItem[] {
  return findings.filter((f) => f.locations.length === 0)
}

/**
 * Choose a column for each pin, then balance.
 *
 * Nearest side keeps the lines short and off the page, but text sits in the
 * left two-thirds of most labels, so every pin would land left. So: assign by
 * nearest side, then move the pins closest to the midline across until the
 * columns are within one of each other — they have the least to lose.
 */
export function assignSides(pins: readonly Pin[]): Map<FindingId, "left" | "right"> {
  const sides = new Map<FindingId, "left" | "right">()
  const left: Pin[] = []
  const right: Pin[] = []
  for (const pin of pins) {
    const side = pin.x < 0.5 ? "left" : "right"
    ;(side === "left" ? left : right).push(pin)
    sides.set(pin.finding.id, side)
  }
  while (Math.abs(left.length - right.length) > 1) {
    const [from, to, toSide] =
      left.length > right.length
        ? ([left, right, "right"] as const)
        : ([right, left, "left"] as const)
    const nearest = [...from].sort((a, b) => Math.abs(a.x - 0.5) - Math.abs(b.x - 0.5))[0]
    if (!nearest) break
    from.splice(from.indexOf(nearest), 1)
    to.push(nearest)
    sides.set(nearest.finding.id, toSide)
  }
  return sides
}

/**
 * Stack each column's cards without overlap. A card holds pin order down its
 * column and drops only as far as the card above forces, so it sits level
 * with its own pin whenever there is room.
 */
export function layoutCards(
  pins: readonly Pin[],
  heights: ReadonlyMap<FindingId, number>,
  pageHeight: number,
): PlacedCard[] {
  const byY = [...pins].sort((a, b) => a.y - b.y)
  const sides = assignSides(byY)
  const bottom: Record<"left" | "right", number> = { left: 0, right: 0 }
  const placed: PlacedCard[] = []
  for (const pin of byY) {
    const id = pin.finding.id
    const side = sides.get(id) ?? "left"
    const height = heights.get(id) ?? ESTIMATED_CARD_HEIGHT
    const top = Math.max(pin.y * pageHeight - height / 2, bottom[side], 0)
    bottom[side] = top + height + CARD_GAP
    placed.push({ id, side, top })
  }
  return placed
}

/* ── The view ──────────────────────────────────────────────────────────── */

export const ZOOM_MIN = 1
export const ZOOM_MAX = 4
export const ZOOM_STEP = 0.25
/** A framed finding fills about this share of the frame's width. */
const FOCUS_FRACTION = 0.34
const FOCUS_ZOOM_MIN = 1.5
const FOCUS_ZOOM_MAX = 3.5

/** The page's zoom and offset inside its frame. At zoom 1 it fills the frame exactly. */
export interface View {
  zoom: number
  tx: number
  ty: number
}

export const FIT: View = { zoom: 1, tx: 0, ty: 0 }

/** Keep the zoom in range and the page covering the frame: no empty margin can be panned into view. */
export function clampView(view: View, width: number, height: number): View {
  const zoom = clamp(view.zoom, ZOOM_MIN, ZOOM_MAX)
  return {
    zoom,
    tx: clamp(view.tx, width - width * zoom, 0),
    ty: clamp(view.ty, height - height * zoom, 0),
  }
}

/** The view that centres `box` at a zoom where it reads comfortably. */
export function frameBox(box: Fraction, width: number, height: number): View {
  // A zero-width box (a point) gets the closest zoom rather than a division by zero.
  const zoom =
    box.w > 0 ? clamp(FOCUS_FRACTION / box.w, FOCUS_ZOOM_MIN, FOCUS_ZOOM_MAX) : FOCUS_ZOOM_MAX
  const cx = (box.x + box.w / 2) * width
  const cy = (box.y + box.h / 2) * height
  return clampView({ zoom, tx: width / 2 - cx * zoom, ty: height / 2 - cy * zoom }, width, height)
}

/** Change the zoom, keeping the point at (`cx`, `cy`) in the frame where it is. */
export function zoomAround(
  view: View,
  nextZoom: number,
  cx: number,
  cy: number,
  width: number,
  height: number,
): View {
  const zoom = clamp(nextZoom, ZOOM_MIN, ZOOM_MAX)
  const ratio = zoom / view.zoom
  return clampView(
    { zoom, tx: cx - (cx - view.tx) * ratio, ty: cy - (cy - view.ty) * ratio },
    width,
    height,
  )
}

/** Where a page point (as fractions) lands in the frame, and whether it is inside it. */
export function project(
  view: View,
  x: number,
  y: number,
  width: number,
  height: number,
): { px: number; py: number; visible: boolean } {
  const px = view.tx + x * width * view.zoom
  const py = view.ty + y * height * view.zoom
  return { px, py, visible: px >= 0 && px <= width && py >= 0 && py <= height }
}
